import { useState } from 'react'
import { createFileRoute, useLocation } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Layout } from '@/components/custom/layout'
import { useTabNavStore } from '@/store/tab-nav-store'
import { getTicketDetail as getTicketDetailApi } from '@/api/ticket'
import { findUser } from '@/api/user'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import TicketDetailPage from '@/components/ticket-detail-page/ticket-detail-page'
import OrganizationDetailPage from '@/components/organization-detail-route/organization-detail-page'
import UserDetailPage from '@/components/user-detail-page/user-detail-page'
import OrganizationCreateModal from '@/components/organization-detail-route/organization-create-modal'
import { getOrganizationDetail } from '@/api/settings'
import { useAllOrganizes } from '@/hooks/use-setting-common'
import { cn } from '@/lib/utils.js'
import { viewGuard } from '@/utils/auth.js'
import { UserRole } from '@/constants/user.js'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  generateAvatarFallback,
} from '@/components/custom/avatar'
import { Building2 } from 'lucide-react'
import { throwException } from '@/exceptions/throw-exception.js'
import QUERY_KEY from '@/constants/use-query-key.js'
import { maskData } from '@/utils/data.js'

export const Route = createFileRoute('/_auth/tickets/$ticketId')({
  beforeLoad: ({ location, params }) => {
    const pathname = location.pathname
    const search = window.location.search
    const tabs = useTabNavStore.getState().tabs
    if (tabs.some((tab) => tab.pathname === pathname)) return

    const { ticketId } = params
    const tabTitle = ticketId.startsWith('new')
      ? 'Ticket mới'
      : `Ticket ${ticketId}`
    useTabNavStore.getState().addTab(pathname, tabTitle, search)
  },
  component: TicketWrapper,
})

function TicketWrapper() {
  const { ticketId } = Route.useParams()
  const location = useLocation()
  const pathname = location.pathname
  const searchParams = new URLSearchParams(location.search)
  const customerId = searchParams.get('customerId')
  const isNewMode = ticketId.startsWith('new')
  return (
    <Ticket
      key={ticketId}
      isNewMode={isNewMode}
      ticketId={ticketId}
      pathname={pathname}
      customerId={customerId}
    />
  )
}

function Ticket({ ticketId, pathname, isNewMode, customerId }) {
  const [tab, setTab] = useState('ticket')
  const [isOpen, setIsOpen] = useState(false)
  const [refreshTime, setRefreshTime] = useState(new Date().getTime())
  const { refetch: refetchOrganizes } = useAllOrganizes()

  const { data: ticket } = useQuery({
    queryKey: [QUERY_KEY.GET_DETAIL_TICKET, { ticketId, refreshTime }],
    queryFn: async () => {
      try {
        const resp = await getTicketDetailApi(ticketId)
        useTabNavStore.getState().updateTabTitle(pathname, resp.data?.subject)
        return resp.data
      } catch (error) {
        throwException(error)
        return {}
      }
    },
    initialData: null,
    enabled: !isNewMode,
  })
  const requesterId = isNewMode ? customerId : ticket?.requester_id

  const { data: requester } = useQuery({
    queryKey: [QUERY_KEY.GET_REQUESTER, { requesterId, refreshTime }],
    queryFn: async () => {
      try {
        const resp = await findUser({ _id: requesterId })
        return resp.data
      } catch (error) {
        throwException(error)
        return {}
      }
    },
    initialData: null,
    enabled: Boolean(requesterId),
  })

  const customerPathname = `/customers/${ticket?.requester_id}`

  const handleChangeTab = (value) => {
    if (!requester?.organize_id && value === 'company') {
      setIsOpen(true)
    } else {
      setTab(value)
    }
  }

  const handleCreateOrganizeSuccess = () => {
    setTab('company')
    setRefreshTime(new Date().getTime())
    refetchOrganizes()
  }

  const { data: organization } = useQuery({
    queryKey: [
      QUERY_KEY.GET_DETAIL_ORGANIZATION,
      { organize_id: requester?.organize_id, refreshTime: refreshTime },
    ],
    queryFn: async () => {
      try {
        const resp = await getOrganizationDetail({ id: requester?.organize_id })
        return resp.data
      } catch (error) {
        throwException(error)
        return {}
      }
    },
    enabled: Boolean(requester?.organize_id),
  })

  const renderNewModeTabs = () => {
    return (
      <Tabs value={tab} onValueChange={(value) => handleChangeTab(value)}>
        <TabsList variant="zenify" className="mb-5">
          {requester && (
            <>
              <TabsTrigger
                variant="zenify"
                value="company"
                className={cn(
                  viewGuard([UserRole.ADMINISTRATOR, UserRole.SUPERVISOR])
                    ? 'leading-7'
                    : 'hidden',
                )}
              >
                <Building2 size={22} className="mr-2" />
                {organization?.name ? organization?.name : 'Tổ chức'}
              </TabsTrigger>
              <TabsTrigger variant="zenify" value="user" className="leading-7">
                <Avatar className="mr-2 h-7 w-7">
                  <AvatarImage src={requester?.avatar} />
                  <AvatarFallback>
                    {requester?.name
                      ? generateAvatarFallback(maskData(requester?.name))
                      : 'A'}
                  </AvatarFallback>
                </Avatar>
                {requester?.name ? maskData(requester?.name) : 'Khách hàng'}
              </TabsTrigger>
            </>
          )}
          <TabsTrigger variant="zenify" value="ticket" className="leading-7">
            Ticket mới
          </TabsTrigger>
        </TabsList>
        {requester && (
          <>
            <TabsContent value="company">
              <OrganizationDetailPage
                pathname={`/settings/organizations/${requester?.organize_id}`}
                organization={organization}
                setRefreshTime={setRefreshTime}
              />
            </TabsContent>
            <TabsContent value="user">
              <UserDetailPage
                setRefreshTime={setRefreshTime}
                customer={requester}
                pathname={customerPathname}
                refreshTime={refreshTime}
              />
            </TabsContent>
          </>
        )}
        <TabsContent value="ticket">
          <TicketDetailPage
            ticket={null}
            isNewMode
            requester={requester}
            pathname={pathname}
            customerPathname={customerPathname}
          />
        </TabsContent>
      </Tabs>
    )
  }

  const renderEditModeTabs = () => {
    return (
      <Tabs value={tab} onValueChange={(value) => handleChangeTab(value)}>
        <TabsList variant="zenify" className="mb-5">
          <TabsTrigger
            variant="zenify"
            value="company"
            className={cn(
              'leading-7',
              viewGuard([UserRole.ADMINISTRATOR, UserRole.SUPERVISOR])
                ? ''
                : 'hidden',
            )}
          >
            <Building2 size={22} className="mr-2" />
            {organization?.name ? organization?.name : 'Tổ chức'}
          </TabsTrigger>
          <TabsTrigger variant="zenify" value="user" className="leading-7">
            <Avatar className="mr-2 h-7 w-7">
              <AvatarImage src={requester?.avatar} />
              <AvatarFallback>
                {requester?.name
                  ? generateAvatarFallback(maskData(requester?.name))
                  : 'A'}
              </AvatarFallback>
            </Avatar>
            {requester?.name ? maskData(requester?.name) : 'Khách hàng'}
          </TabsTrigger>
          <TabsTrigger variant="zenify" value="ticket" className="leading-7">
            {ticket?.code ? `Ticket ${ticket?.code}` : 'Ticket'}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="company">
          <OrganizationDetailPage
            pathname={`/settings/organizations/${requester?.organize_id}`}
            organization={organization}
            setRefreshTime={setRefreshTime}
          />
        </TabsContent>
        <TabsContent value="user">
          <UserDetailPage
            setRefreshTime={setRefreshTime}
            customer={requester}
            pathname={customerPathname}
            refreshTime={refreshTime}
          />
        </TabsContent>
        <TabsContent value="ticket">
          <TicketDetailPage
            refreshTime={refreshTime}
            setRefreshTime={setRefreshTime}
            ticket={ticket}
            isNewMode={false}
            requester={requester}
            pathname={pathname}
            customerPathname={customerPathname}
          />
        </TabsContent>
      </Tabs>
    )
  }

  return (
    <Layout.Body>
      {isNewMode ? renderNewModeTabs() : renderEditModeTabs()}
      <OrganizationCreateModal
        setIsOpen={setIsOpen}
        isOpen={isOpen}
        customerId={requesterId}
        handleCreateSuccess={handleCreateOrganizeSuccess}
      />
    </Layout.Body>
  )
}
